import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

export type FieldType = "text" | "longtext" | "number" | "integer" | "money" | "date" | "datetime" | "time" | "choice" | "bool";

export interface Field {
  name: string;
  label: string;
  type: FieldType;
  options?: string[];
  unit?: string;
  help?: string;
  required?: boolean;
  default?: unknown;
}

export interface Collection {
  name: string;
  label: string;
  described: boolean;
  fields: Field[];
  rows: number;
}

export type Cell = string | number | null;

interface ViewBase {
  id: string;
  label?: string;
  width?: "half" | "full";
  /** Set when the view's query failed; then there is no data. */
  error?: string;
}

/** A button that sends the model a request: answered in the quick box, or opened as a chat. Row actions fill in {column}. */
export interface Action {
  label: string;
  prompt: string;
  chat?: boolean;
}

export type View = ViewBase &
  (
    | { type: "stat"; sql: string; unit?: string; decimals?: number; data?: { value: Cell } }
    | { type: "progress"; sql: string; target?: number; target_sql?: string; unit?: string; decimals?: number; data?: { value: Cell; target: Cell } }
    | { type: "chart"; sql: string; chart?: "bar" | "line" | "pie"; unit?: string; data?: { columns: string[]; rows: Cell[][] } }
    | { type: "table"; sql: string; actions?: Action[]; data?: { columns: string[]; rows: Cell[][] } }
    | { type: "calendar"; sql: string; days?: number; data?: { columns: string[]; rows: Cell[][]; days: number } }
    // rows start with the record id (column "_id"); `fields` describes the other columns
    | { type: "records"; collection: string; fields?: string[]; where?: string; order?: string; limit?: number; actions?: Action[]; data?: { columns: string[]; rows: Cell[][]; fields: Field[]; collection: string } }
    // rows are [id, label, done]
    | { type: "checklist"; collection: string; label_field: string; done_field: string; where?: string; data?: { columns: string[]; rows: Cell[][]; collection: string } }
    | { type: "form"; collection: string; fields?: string[]; submit?: string; data?: { fields: Field[]; collection: string } }
    | { type: "note"; text: string; data?: Record<string, never> }
    | { type: "prompts"; buttons: Action[]; data?: Record<string, never> }
  );

export interface Rule {
  id: string;
  name: string;
  action: "agent" | "notify" | "checkin";
  when: string;
  condition: { sql: string; when: "rows" | "none" } | null;
  enabled: boolean;
  last_status: string | null;
  next_run_at: string | null;
}

export interface SpaceSettings {
  model_edits_instructions?: boolean;
  generic_notifications?: boolean;
  exposed?: boolean;
  day_ends?: number;
  model_id?: string;
  search_files?: boolean;
}

/** How much of a space is indexed for search by meaning (GET /api/spaces/{id}/search). */
export interface SearchStatus {
  enabled: boolean;
  records?: number;
  records_waiting?: number;
  files?: number;
  files_waiting?: number;
  error?: string;
}

export interface Space {
  id: string;
  name: string;
  description: string;
  instructions: string;
  settings: SpaceSettings;
  role: "owner" | "member";
  rules: Rule[];
  views: View[];
  collections: Collection[];
  notes: string;
}

export interface SpaceSummary {
  id: string;
  name: string;
  description: string;
  role: "owner" | "member";
  owner: string;
  created_at: string;
}

export interface HistoryItem {
  batch: string;
  id: number;
  at: string;
  actor: string;
  source: string;
  summary: string;
  sql: string | null;
  undoable: boolean;
  undone: boolean;
}

export interface Member {
  user_id: string;
  name: string;
  username: string;
  role: "owner" | "member";
}

export function useSpaces() {
  return useQuery({ queryKey: ["spaces"], queryFn: () => api.get<SpaceSummary[]>("/api/spaces"), staleTime: 30_000 });
}

export function useSpace(id: string | undefined | null) {
  return useQuery({ queryKey: ["space", id], queryFn: () => api.get<Space>(`/api/spaces/${id}`), enabled: !!id });
}

/** API calls on a space's records and views. Each returns the change's id ("batch"), which undo takes. */
export const spaceApi = {
  addRecord: (space: string, collection: string, values: Record<string, unknown>) =>
    api.post<{ id: number; batch: string }>(`/api/spaces/${space}/records/${encodeURIComponent(collection)}`, { values }),
  updateRecord: (space: string, collection: string, id: number, values: Record<string, unknown>) =>
    api.patch<{ id: number; batch: string }>(`/api/spaces/${space}/records/${encodeURIComponent(collection)}/${id}`, { values }),
  deleteRecord: (space: string, collection: string, id: number) => api.delete<{ batch: string }>(`/api/spaces/${space}/records/${encodeURIComponent(collection)}/${id}`),
  clearChecklist: (space: string, viewId: string) => api.post<{ deleted: number; batch: string | null }>(`/api/spaces/${space}/views/${viewId}/clear`),
  undo: (space: string, batch: string) => api.post<{ batch: string }>(`/api/spaces/${space}/undo/${batch}`),
};

/** A value as the user reads it. */
export function formatValue(value: Cell, field?: Pick<Field, "type" | "unit">, decimals?: number): string {
  if (value === null || value === undefined || value === "") return "";
  if (field?.type === "bool") return value ? "Yes" : "No";
  if (typeof value === "number") {
    const text =
      field?.type === "money"
        ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : value.toLocaleString(undefined, decimals !== undefined ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals } : { maximumFractionDigits: 2 });
    return field?.unit ? `${text} ${field.unit}` : text;
  }
  return field?.unit ? `${value} ${field.unit}` : String(value);
}
